import CurriculumManager from '../../lib/CurriculumManager';
import { GAME_ID, ITEMS_IN_ROUND, ITEMS_IN_35_ROUND, G35_LEVELS_START_INDEX } from '../../data/constants';

export const curriculumManager = new CurriculumManager();

const initialState = {
  onboarding: [],
  standardSet: [],
  progressBarIndex: 0,
  standardIndex: 0, // What is the current question item count in current round
  onboardingIndex: 0,
  currentLevel: 0,
  levelCount: 0,
  heardTypeTwoNarration: false,

  seenTypeTwoOnboarding: false,
  onlyDoingOnboarding: false,

  isOnboardingAnswerFlashing: false,

  isDoneButtonEnabled: false,

  positionInContent: 0,
};

/**
 * Really basic curriculum reducer, nothing super special here.
 * Also handles some progress tracking, b/c both the curriculumManager
 * and the progress logic need to know about how many / which questions have been answered correctly.
 *
 * Quick Ref:
 * LOAD_CURRICULUM
 * LOAD_NEW_LEVEL
 */
const curriculumReducer = (
  state = initialState,
  { type, gameId, payload, stateData },
) => {
  if (gameId !== GAME_ID) {
    return state;
  }
  let itemsInRound = ITEMS_IN_ROUND;
  if (state.currentLevel >= G35_LEVELS_START_INDEX + 1) {
    itemsInRound = ITEMS_IN_35_ROUND;
  }

  switch (type) {
    case 'REQUESTRESPONSE_GAMESTATE': {
      const data = stateData.sloppy_scrolls.GameData;
      if (data === undefined || (data.gameDetails && data.gameDetails.masteredItems === undefined)) {
        return {
          ...state,
          // Do not overwrite onboarding curriculum if it has already been loaded, or else game crashes
          // when users click the '?' button to go immediately to onboarding. AQUA-7797
          onboarding: state.onboarding,
        };
      }
      return {
        ...state,
        seenTypeTwoOnboarding: data.gameDetails.seenTypeTwoOnboarding || false,
        onboarding: curriculumManager.getOnboarding(),
        positionInContent: data.gameDetails.positionInContent || 0,
      };
    }
    // Loads a curriculum with level 1 as the default level,
    // gets a standardSet containing errors, their indices, and words
    // in the paragraph
    case 'LOAD_CURRICULUM':
      curriculumManager.loadCurriculum(payload.curriculumJson);
      curriculumManager.populateOnboarding();
      curriculumManager.populateStandardSet(1);
      return {
        ...state,
        onboarding: curriculumManager.getOnboarding(),
        standardSet: curriculumManager.getStandardSet(),
        levelCount: curriculumManager.getLevelCount(),
      };
    case 'SET_ONLY_DOING_ONBOARDING':
      return {
        ...state,
        onlyDoingOnboarding: payload,
      };
    case 'FINISH_SEEING_ONBOARDING': {
      return {
        ...state,
        seenTypeOneOnboarding: true,
        seenTypeTwoOnboarding: true,
      };
    }
    case 'HEAR_OPENING_NARRATION': {
      const typeTwo = state.heardTypeTwoNarration;
      return {
        ...state,
        heardTypeTwoNarration: typeTwo,
      };
    }
    // Completely exchanges the curriculum data for a new level
    case 'LOAD_NEW_LEVEL':
      curriculumManager.populateOnboarding(payload.level);
      curriculumManager.populateStandardSet(
        payload.level,
        state.positionInContent,
      );
      return {
        ...state,
        onboarding: curriculumManager.getOnboarding(),
        standardSet: curriculumManager.getStandardSet(state.contentStartPoint),
        currentLevel: payload.level,
        isDoneButtonEnabled: payload.level < G35_LEVELS_START_INDEX + 1,
      };
    case 'SUBMIT_RESPONSE':
      return {
        ...state,
        isDoneButtonEnabled: false,
      };
    case 'INCREMENT_ONBOARDING_INDEX':
      return {
        ...state,
        onboardingIndex: state.onboardingIndex + 1,
      };
    case 'CLEAR_ONBOARDING_INDEX':
      return {
        ...state,
        onboardingIndex: 0,
      };
    case 'INCREMENT_STANDARD_INDEX':
      return {
        ...state,
        standardIndex: Math.min(state.standardIndex + 1, itemsInRound - 1), // Do not go higher than number of items
        positionInContent: state.positionInContent + 1,
        isDoneButtonEnabled: state.currentLevel < G35_LEVELS_START_INDEX + 1,
      };
    case 'CLEAR_STANDARD_INDEX':
      return {
        ...state,
        standardIndex: 0,
        progressBarIndex: 0,
      };

    case 'INCREMENT_PROGRESS_BAR_INDEX':
      return {
        ...state,
        progressBarIndex: state.progressBarIndex + 1,
      };
    case 'FLASH_ONBOARDING_ANSWER': {
      return {
        ...state,
        isOnboardingAnswerFlashing: payload.setFlash,
      };
    }
    case 'SET_DONE_BUTTON_ENABLED': {
      return {
        ...state,
        isDoneButtonEnabled: payload.isDoneButtonEnabled,
      };
    }
    case 'COMPLETE_ROUND': {
      if (payload.earlyExit) {
        return {
          ...state,
          positionInContent: state.positionInContent < itemsInRound ?
            0 :
            itemsInRound,
        };
      }
      return { ...state };
    }
    default:
      return state;
  }
};

export default curriculumReducer;
